
import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Category {
  id: string;
  name: string;
}

interface CategorySelectProps {
  floorPlanId: string;
  onCategoriesChange?: (categories: Category[]) => void;
}

export function CategorySelect({ floorPlanId, onCategoriesChange }: CategorySelectProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const { user } = useAuth();
  const { toast } = useToast();

  // Laad alle beschikbare categorieën
  useEffect(() => {
    const loadCategories = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    // Laad de huidige categorieën voor deze plattegrond
    const loadFloorPlanCategories = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('floor_plan_categories')
          .select(`
            category_id,
            categories:category_id (
              id,
              name
            )
          `)
          .eq('floor_plan_id', floorPlanId);

        if (error) throw error;

        const formattedCategories = data
          .map((item: any) => item.categories)
          .filter((category): category is Category => category !== null);

        setSelectedCategories(formattedCategories);
        onCategoriesChange?.(formattedCategories);
      } catch (error) {
        console.error('Error loading floor plan categories:', error);
      }
    };

    loadCategories();
    loadFloorPlanCategories();
  }, [user, floorPlanId]);

  const handleAddCategory = async (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category || selectedCategories.some(c => c.id === categoryId)) return;

    try {
      const { error } = await supabase
        .from('floor_plan_categories')
        .insert({
          floor_plan_id: floorPlanId,
          category_id: categoryId
        });

      if (error) throw error;

      const updatedCategories = [...selectedCategories, category];
      setSelectedCategories(updatedCategories);
      onCategoriesChange?.(updatedCategories);

      toast({
        title: "Categorie toegevoegd",
        description: `${category.name} is toegevoegd aan de plattegrond`,
      });
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: "Fout bij toevoegen",
        description: "Er is een fout opgetreden bij het toevoegen van de categorie",
        variant: "destructive",
      });
    }
  };

  const handleRemoveCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('floor_plan_categories')
        .delete()
        .eq('floor_plan_id', floorPlanId)
        .eq('category_id', categoryId);

      if (error) throw error;

      const updatedCategories = selectedCategories.filter(c => c.id !== categoryId);
      setSelectedCategories(updatedCategories);
      onCategoriesChange?.(updatedCategories);

      toast({
        title: "Categorie verwijderd",
        description: "De categorie is verwijderd van de plattegrond",
      });
    } catch (error) {
      console.error('Error removing category:', error);
      toast({
        title: "Fout bij verwijderen",
        description: "Er is een fout opgetreden bij het verwijderen van de categorie",
        variant: "destructive",
      });
    }
  };

  const availableCategories = categories.filter(
    category => !selectedCategories.some(sc => sc.id === category.id)
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {selectedCategories.map((category) => (
          <Badge key={category.id} variant="secondary" className="gap-1">
            {category.name}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={() => handleRemoveCategory(category.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>
      {availableCategories.length > 0 && (
        <Select onValueChange={handleAddCategory}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Voeg een categorie toe..." />
          </SelectTrigger>
          <SelectContent>
            {availableCategories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
